/*
 * Snap carousel arrows (MFS-5676) — prev/next buttons for native scroll-snap
 * tracks (club landings B and C). No slider library: the track is an
 * ordinary overflow-x scroller, swipe / trackpad / keyboard scrolling stay
 * native, this file only drives the arrows.
 *
 *   [data-snap-carousel]      root; data-snap-step="card" (default) scrolls by
 *                             the first card's width + the track's column gap,
 *                             "page" by the track's visible width
 *   [data-snap-track]         the scrolling element (its children are the cards)
 *   [data-snap-prev]/[data-snap-next]  arrow buttons, disabled (disabled +
 *                             aria-disabled) at the edges
 * The root gets data-snap-scrollable="true|false" so CSS can hide arrows when
 * everything fits. prefers-reduced-motion -> instant jumps. Initialised on
 * DOMContentLoaded and on shopify:section:load (idempotent per root), torn
 * down on shopify:section:unload.
 *
 * Mouse drag: a desktop mouse can't swipe and its vertical wheel doesn't move
 * a horizontal scroller, so a click-and-drag scrolls the track (mouse only —
 * touch, pen and trackpad scrolling stay native). While dragging the root has
 * data-snap-dragging (CSS: grabbing cursor, no text selection); on release
 * the track settles on the nearest card and scroll-snap takes over again. A
 * drag never counts as a click on a card link or button.
 */
(function () {
  'use strict';

  if (window.SnapCarousel) return;

  var SELECTOR = {
    root: '[data-snap-carousel]',
    track: '[data-snap-track]',
    prev: '[data-snap-prev]',
    next: '[data-snap-next]',
  };

  // Sub-pixel scroll positions never land exactly on 0 / max.
  var EDGE_TOLERANCE = 2;

  // Pointer travel (px) before a mouse press becomes a drag, not a click.
  var DRAG_THRESHOLD = 6;

  // Share of a card a drag must cover to move on to the next card.
  var DRAG_FLICK = 0.2;

  // Fallback for browsers without the scrollend event.
  var SETTLE_TIMEOUT = 500;

  var instances = [];

  function prefersReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function firstCard(track) {
    var children = track.children;
    for (var i = 0; i < children.length; i += 1) {
      if (children[i].getBoundingClientRect().width > 0) return children[i];
    }
    return null;
  }

  function stepSize(root, track) {
    if (root.getAttribute('data-snap-step') === 'page') return track.clientWidth;
    var card = firstCard(track);
    if (!card) return track.clientWidth;
    var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function setDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function create(root) {
    var track = root.querySelector(SELECTOR.track);
    if (!track) return null;
    var prev = root.querySelector(SELECTOR.prev);
    var next = root.querySelector(SELECTOR.next);
    var frame = 0;
    var observer = null;

    // The first snap position is not always 0: with side padding on the track
    // the browser snaps the first card to its scroll-padding, e.g. 20px in.
    function startEdge() {
      var card = firstCard(track);
      if (!card) return 0;
      var padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      var left = card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      return Math.max(0, left - padding);
    }

    function sync() {
      frame = 0;
      var max = track.scrollWidth - track.clientWidth;
      var start = Math.min(startEdge(), max);
      var scrollable = max - start > EDGE_TOLERANCE;
      setDisabled(prev, !scrollable || track.scrollLeft <= start + EDGE_TOLERANCE);
      setDisabled(next, !scrollable || track.scrollLeft >= max - EDGE_TOLERANCE);
      root.setAttribute('data-snap-scrollable', scrollable ? 'true' : 'false');
    }

    // Scroll and resize fire in bursts — one sync per frame.
    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(sync);
    }

    function go(direction) {
      track.scrollBy({
        left: direction * stepSize(root, track),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    function onPrev() { go(-1); }
    function onNext() { go(1); }

    // Mouse drag ----------------------------------------------------------
    var drag = { id: null, x: 0, left: 0, active: false, moved: 0 };
    var suppressClick = false;
    var settleTimer = 0;

    // Scroll positions the browser snaps to: each card's start minus the
    // track's scroll-padding, clamped to the scrollable range.
    function snapPoints() {
      var max = track.scrollWidth - track.clientWidth;
      var padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      var origin = track.getBoundingClientRect().left - track.scrollLeft;
      var points = [];
      for (var i = 0; i < track.children.length; i += 1) {
        var box = track.children[i].getBoundingClientRect();
        if (!box.width) continue;
        points.push(Math.min(max, Math.max(0, box.left - origin - padding)));
      }
      return points;
    }

    function restoreSnap() {
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = 0;
      track.removeEventListener('scrollend', restoreSnap);
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
    }

    function settle(direction) {
      var card = firstCard(track);
      var bias = direction * (card ? card.getBoundingClientRect().width : 0) * DRAG_FLICK;
      var aim = track.scrollLeft + bias;
      var points = snapPoints();
      var target = track.scrollLeft;
      var best = Infinity;
      points.forEach(function (point) {
        var distance = Math.abs(point - aim);
        if (distance < best) { best = distance; target = point; }
      });
      if (Math.abs(target - track.scrollLeft) < 1) { restoreSnap(); return; }
      if ('onscrollend' in window) track.addEventListener('scrollend', restoreSnap);
      settleTimer = window.setTimeout(restoreSnap, SETTLE_TIMEOUT);
      track.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    function onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (track.scrollWidth - track.clientWidth <= EDGE_TOLERANCE) return;
      restoreSnap();
      drag.id = e.pointerId;
      drag.x = e.clientX;
      drag.left = track.scrollLeft;
      drag.active = false;
      drag.moved = 0;
      suppressClick = false;
    }

    function onPointerMove(e) {
      if (e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      if (!drag.active) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.active = true;
        if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
        // Free movement while the pointer is down; snapping resumes on release
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        root.setAttribute('data-snap-dragging', '');
        if (window.getSelection) window.getSelection().removeAllRanges();
      }
      drag.moved = dx;
      track.scrollLeft = drag.left - dx;
    }

    function onPointerUp(e) {
      if (e.pointerId !== drag.id) return;
      drag.id = null;
      if (!drag.active) return;
      drag.active = false;
      suppressClick = true;
      root.removeAttribute('data-snap-dragging');
      if (track.releasePointerCapture && track.hasPointerCapture && track.hasPointerCapture(e.pointerId)) {
        track.releasePointerCapture(e.pointerId);
      }
      // Dragging to the left moves the track forward
      settle(drag.moved < 0 ? 1 : -1);
    }

    // The click that ends a drag must not open a card or press its button
    function onClickCapture(e) {
      if (!suppressClick) return;
      suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    }

    // Native image / link dragging would cancel the pointer stream
    function onDragStart(e) {
      e.preventDefault();
    }

    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    track.addEventListener('scroll', schedule, { passive: true });
    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onClickCapture, true);
    track.addEventListener('dragstart', onDragStart);
    window.addEventListener('resize', schedule);
    // Images and fonts change the track's scroll width after load.
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(schedule);
      observer.observe(track);
    }
    sync();

    return {
      root: root,
      update: schedule,
      destroy: function () {
        if (prev) prev.removeEventListener('click', onPrev);
        if (next) next.removeEventListener('click', onNext);
        track.removeEventListener('scroll', schedule);
        track.removeEventListener('pointerdown', onPointerDown);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', onPointerUp);
        track.removeEventListener('pointercancel', onPointerUp);
        track.removeEventListener('click', onClickCapture, true);
        track.removeEventListener('dragstart', onDragStart);
        window.removeEventListener('resize', schedule);
        restoreSnap();
        root.removeAttribute('data-snap-dragging');
        if (observer) observer.disconnect();
        if (frame) window.cancelAnimationFrame(frame);
        delete root.snapCarousel;
      },
    };
  }

  function init(root) {
    if (!root || root.snapCarousel) return root ? root.snapCarousel : null;
    var instance = create(root);
    if (!instance) return null;
    root.snapCarousel = instance;
    instances.push(instance);
    return instance;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR.root).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    instances = instances.filter(function (instance) {
      if (!e.target.contains(instance.root)) return true;
      instance.destroy();
      return false;
    });
  });

  window.SnapCarousel = {
    init: init,
    initAll: initAll,
  };
})();
